import { Navigate } from 'react-router-dom'
import { logout as timeclockWidgetLogout } from '@loadedhub/timeclock-management'
import { logout as onboardingLogout } from '@loadedhub/team-onboarding-management'
import { logout as rosteringLogout } from '@loadedhub/rostering-management'

import { appConfig, logger } from '$utils'

export class AuthError extends Error {
  constructor(message) {
    super(message)
  }
}

interface AuthTokenData {
  accessToken: string
  refreshToken: string
}

/**
 * Checks if the user is authenticated and redirects to the login page if not.
 */
export function checkAuthentictionOrRedirectToLogin() {
  if (!isAuthenticated()) {
    logger.info('Redirecting to login due to missing authentication')
    const urlEncodedRoute = encodeURIComponent('/' + window.location.hash)
    Navigate({
      to: `/ui/login?redirect=${urlEncodedRoute}`,
    })
  }
}

/**
 * Checks if the user is authenticated by looking for an access token in the
 * query parameters or the local storage.
 *
 * @returns true if the user is authenticated, false otherwise
 */
export function isAuthenticated() {
  return !!getAuthToken()
}

/**
 * Stores the access and refresh tokens in local storage.
 *
 * @param accessToken The access token to store
 * @param refreshToken The refresh token to store
 */
export function setAuthTokens(accessToken: string, refreshToken: string) {
  storeAuthenticationData({
    accessToken,
    refreshToken,
  })
}

/**
 * Gets the access token from the query parameters or local storage.
 *
 * @returns The access token
 */
export function getAuthToken() {
  // If there is a query parameter with key of 't' then use that as access token
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('t')) {
    return urlParams.get('t')
  }

  const { accessToken } = getAuthenticationData()

  if (accessToken) {
    return accessToken
  }

  // if there isn't any auth data, we actually want to just return nothing here,
  // this flow occurs when running requests for unauthenticated users (e.g. login).
}

/**
 * Gets the authentication data from local storage.
 *
 * @returns The authentication data
 */
export function getAuthData() {
  const rawAuthData = window.localStorage.getItem('ls.authorizationData')
  if (rawAuthData) {
    const authData = JSON.parse(rawAuthData)
    if (!authData.accessToken) {
      throw new AuthError('unable to read stored token')
    }
    return authData
  }
}

/**
 * Gets the company id from local storage.
 *
 * @returns The company id
 */
export function getCompanyId() {
  const rawCompanyId = window.localStorage.getItem('ls.defaultCompany')
  if (rawCompanyId) {
    const companyId = JSON.parse(rawCompanyId)
    if (!companyId) {
      throw new AuthError('unable to read stored company id')
    }
    return companyId
  }
}

function getAuthenticationData() {
  return JSON.parse(localStorage.getItem(`ls.authorizationData`) || '{}')
}

function storeAuthenticationData(data: AuthTokenData) {
  localStorage.setItem(`ls.authorizationData`, JSON.stringify(data))
}

function clearAuthenticationData() {
  localStorage.removeItem(`ls.authorizationData`)
}

/**
 * Handle cleaning up the auth session completely. Once done, this will force
 * the browser to update as we want to replace the page content with the
 * unauthenticated user default.
 */
export function signout() {
  logger.info('Processing logout request')

  // Clean up any associated widget sessions. Each logout call below follows the
  // same pattern: it's async but deliberately not awaited — we don't want to
  // block logout and we continue even if a call fails. Internally each call
  // keeps its network request alive across the page navigation below.
  const timeclockV2ApiBaseUrl = appConfig.get<string>('timeclockV2ApiUrl')
  if (timeclockV2ApiBaseUrl) {
    timeclockWidgetLogout(timeclockV2ApiBaseUrl)
  }

  const onboardingApiBaseUrl = appConfig.get<string>('onboardingApiUrl')
  if (onboardingApiBaseUrl) {
    onboardingLogout(onboardingApiBaseUrl)
  }

  const rosteringApiBaseUrl = appConfig.get<string>('rosteringApiUrl')
  if (rosteringApiBaseUrl) {
    rosteringLogout(rosteringApiBaseUrl)
  }

  // remove data from storage
  clearAuthenticationData()

  // and finally reload the root url to replace the page content
  window.location.assign('/')
}

const concurrentRefreshQueue = {
  refreshing: false,
  awaiters: new Array<{
    resolve: (value: unknown) => void
    reject: (reason?: any) => void
  }>(),
}

/**
 * Refreshes the access token by exchanging the refresh token for a new access
 * token.
 *
 * @param clearStorageOnFailure Whether to clear the authentication data if the
 * refresh fails
 * @returns The new access token
 */
export async function refreshAccessToken(
  clearStorageOnFailure: boolean = true
) {
  // We end up with a call into this method for each API call that is made
  // concurrently when a token expires - e.g. refreshing the page with an
  // expired access token. To avoid each API request individually triggering a
  // refresh token exchange (which will cause errors as the refresh tokens are
  // one time use), we allow the first request to run to completion and the
  // others simply get released once the first has finished. This way they all
  // end up using the freshly updated access token once it's ready.
  if (concurrentRefreshQueue.refreshing) {
    return new Promise((resolve, reject) => {
      concurrentRefreshQueue.awaiters.push({ resolve, reject })
    })
  }

  concurrentRefreshQueue.refreshing = true

  const { refreshToken } = getAuthenticationData()

  if (!refreshToken) {
    throw new Error('No refresh token available to renew auth credentials')
  }

  try {
    const params = new URLSearchParams()
    params.append('grant_type', 'refresh_token')
    params.append('client_id', 'mercury')
    params.append('refresh_token', refreshToken)

    // Send token and code verifier to backend
    const response = await fetch(
      `${appConfig.get<string>('mercuryApiUrl')}/token`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      }
    )

    if (!response.ok) {
      throw new AuthError('API client is not authenticated')
    }

    const contentType = response.headers.get('Content-Type')
    if (!contentType?.includes('application/json')) {
      throw new AuthError('API client is not authenticated')
    }

    const data = await response.json()

    storeAuthenticationData({
      accessToken: data['access_token'],
      refreshToken: data['refresh_token'],
    })

    // Any queued refresh requests can now be released - they'll pick
    // up the value that this execution has just refreshsed
    concurrentRefreshQueue.awaiters.forEach(({ resolve }) => resolve(undefined))
    concurrentRefreshQueue.awaiters = []
  } catch (error) {
    console.error('Error refreshing token:', error)

    // We clear any stored auth data so the user is forced to re-authenticate.
    // The only way we expect to end up here is with dodgy auth data that isn't
    // going to ever work.
    if (clearStorageOnFailure) {
      clearAuthenticationData()
    }

    // Reject any queued refresh requests, they'll fail too if this execution did.
    concurrentRefreshQueue.awaiters.forEach(({ reject }) => reject(error))
    concurrentRefreshQueue.awaiters = []

    throw new AuthError('Unable to refresh token')
  } finally {
    // No one is performing a refresh now, so reset this to make sure any future
    // calls won't queue perpetually.
    concurrentRefreshQueue.refreshing = false
  }
}
